import { usePostAuthForgotPasswordMutation } from '@/api/serverApi'
import { BrandBackground, Button, EmailSentIcon, InputLabel, PadlockIcon, TextInput, useErrorHandler } from '@cfs-fixings/shared'
import { valibotResolver } from '@hookform/resolvers/valibot'
import { useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import * as v from 'valibot'

const forgotPasswordFormSchema = v.object({
  email: v.string([v.email('Invalid email address')]),
})

type ForgotPasswordFormInputs = v.Input<typeof forgotPasswordFormSchema>

const textSpec = {
  title: 'Forgot Your Password?',
  subtitle: 'Enter your account email and we\'ll send you password reset instructions.',
  emailField: {
    label: 'EMAIL ADDRESS',
    placeholder: 'Email',
  },
  resetPasswordButtonLabel: 'Reset Password',
  complete: {
    title: 'Check Your Inbox',
    content: 'If we found your account, we\'ve sent instructions on how to reset your password to ',
  },
}

function ForgotPasswordForm () {
  const errorHandler = useErrorHandler()
  const [hasReset, setHasReset] = useState(false)

  const { register, handleSubmit, formState: { errors }, setError, watch } = useForm<ForgotPasswordFormInputs>({
    defaultValues: {
      email: '',
    },
    resolver: valibotResolver(forgotPasswordFormSchema),
  })

  const [forgotPassword, { isLoading: isSubmitting }] = usePostAuthForgotPasswordMutation()

  const onSubmit: SubmitHandler<ForgotPasswordFormInputs> = (data) => {
    forgotPassword({ body: data })
      .unwrap()
      .then((_) => {
        setHasReset(true)
      })
      .catch((error) => {
        errorHandler(error, setError)
      })
  }

  if (hasReset) {
    return (
      <div className="flex justify-center w-full mt-[110px]">
        <section className="flex flex-col items-center min-w-[380px]">
          <BrandBackground className="w-[50px] h-[50px]">
            <EmailSentIcon className="h-[28px] w-[28px] text-grey-800" />
          </BrandBackground>

          <h1 className="mb-[15px] mt-[30px] text-4xl leading-[43.2px] font-[450]">{textSpec.complete.title}</h1>
          <p className="mb-[50px] text-base leading-[22.4px] text-center">{textSpec.complete.content}<span className="font-bold">{watch('email')}</span></p>
        </section>
      </div>
    )
  }

  return (
    <form
      className="flex flex-col items-center w-full max-w-[380px]"
      onSubmit={handleSubmit(onSubmit)}
    >
      <BrandBackground className="w-[50px] h-[50px]">
        <PadlockIcon className="h-[32px] w-[32px] text-grey-800" />
      </BrandBackground>

      <h1 className="mb-[15px] mt-[30px] text-4xl leading-[43.2px] font-[450]">{textSpec.title}</h1>

      <p className="mb-[50px] text-base leading-[22.4px] text-center">{textSpec.subtitle}</p>

      <div className="mb-10 w-full">
        <InputLabel name="email">{textSpec.emailField.label}</InputLabel>
        <TextInput
          placeholder={textSpec.emailField.placeholder}
          error={errors?.email?.message}
          {...register('email')}
        />
      </div>

      <div className="mt-2 w-full">
        <Button
          variant="dark"
          isLoading={isSubmitting}
          type="submit"
          isBlock
        >
          {textSpec.resetPasswordButtonLabel}
        </Button>
      </div>
    </form>
  )
}

export default ForgotPasswordForm
